/**
 * Offline audio cache for the installed PWA.
 * Full responses live in Cache Storage (graystore.v1-0/audio) and are served
 * by /sw.js with Range support so <audio> can seek without re-fetching.
 */

/** Must match AUDIO bucket in public/sw.js (graystore.v1-0). */
export const AUDIO_CACHE_NAME = 'graystore.v1-0/audio';

export type AudioCacheStats = {
  count: number;
  bytes: number;
};

function canUseCaches(): boolean {
  return typeof caches !== 'undefined';
}

/** True when running as an installed PWA (standalone / iOS home screen). */
export function isInstalledPwa(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone)
  );
}

export async function hasCachedAudio(url: string): Promise<boolean> {
  if (!canUseCaches() || !url) return false;
  try {
    const cache = await caches.open(AUDIO_CACHE_NAME);
    return Boolean(await cache.match(url, { ignoreVary: true }));
  } catch {
    return false;
  }
}

/**
 * Download a complete (non-Range) copy into the audio cache.
 * Safe to call while playback uses the network URL — does not block play.
 */
export async function cacheAudioUrl(url: string): Promise<boolean> {
  if (!canUseCaches() || !url) return false;
  if (!/^https?:\/\//i.test(url)) return false;
  try {
    const cache = await caches.open(AUDIO_CACHE_NAME);
    if (await cache.match(url, { ignoreVary: true })) return true;
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      // Prefer a full body; Range would produce an uncacheable 206.
      headers: { Accept: 'audio/*,*/*;q=0.1' },
    });
    if (!res.ok || res.status !== 200) return false;
    const ct = res.headers.get('Content-Type') || '';
    if (ct && !/^(audio|application\/octet|binary)\b/i.test(ct) && !/\bmpeg\b/i.test(ct)) {
      // Allow empty/unknown types from our media host.
      if (!/media\.thisiszacharygray\.com|\/stream\//i.test(url)) return false;
    }
    await cache.put(url, res);
    return true;
  } catch {
    return false;
  }
}

export async function getAudioCacheStats(): Promise<AudioCacheStats> {
  if (!canUseCaches()) return { count: 0, bytes: 0 };
  try {
    const cache = await caches.open(AUDIO_CACHE_NAME);
    const keys = await cache.keys();
    let bytes = 0;
    for (const req of keys) {
      const res = await cache.match(req);
      if (!res) continue;
      const cl = res.headers.get('Content-Length');
      if (cl && Number.isFinite(Number(cl))) {
        bytes += Number(cl);
        continue;
      }
      try {
        bytes += (await res.clone().arrayBuffer()).byteLength;
      } catch {
        /* ignore */
      }
    }
    return { count: keys.length, bytes };
  } catch {
    return { count: 0, bytes: 0 };
  }
}

export async function clearAudioCache(): Promise<void> {
  if (!canUseCaches()) return;
  try {
    await caches.delete(AUDIO_CACHE_NAME);
  } catch {
    /* ignore */
  }
}

/** Clear graystore shell/runtime caches (keeps audio until clearAudioCache). */
export async function clearAppShellCaches(): Promise<void> {
  if (!canUseCaches()) return;
  try {
    const names = await caches.keys();
    await Promise.all(
      names
        .filter((n) => n !== AUDIO_CACHE_NAME)
        .map((n) => caches.delete(n)),
    );
  } catch {
    /* ignore */
  }
}

export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
